import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { CustomerPaymentMethodFormSchemaValues } from 'src/features/paymentMethods/utils/formSchema';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { PAYMENT_METHOD_TYPE } from 'src/features/paymentMethods/utils/types';
import { PAYMENT_TYPE_METHODS_TRANSLATION_MAP } from 'src/features/paymentMethods/utils/constants';
import { paymentMethodsMessages } from 'src/features/paymentMethods/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useListCustomers } from 'src/features/customers/api/useListCustomers';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { useParams } from 'react-router-dom';
import React from 'react';

type CustomerPaymentMethodFormProps = {
    form: UseFormReturn<CustomerPaymentMethodFormSchemaValues>;
    onSubmit: (values: CustomerPaymentMethodFormSchemaValues) => void;
};

function CustomerPaymentMethodForm({ onSubmit, form }: CustomerPaymentMethodFormProps) {
    const { formatMessage } = useIntl();
    const { customerId } = useParams();

    const disableCustomerSelect = Boolean(customerId);
    const [searchCustomer, setSearchCustomer] = React.useState<string>('');

    const { data: customersData } = useListCustomers({
        search: searchCustomer,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<CustomerPaymentMethodFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.validFrom)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: new Date() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<CustomerPaymentMethodFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(commonMessages.type)}
                options={Object.values(PAYMENT_METHOD_TYPE)}
                optionTranslationMap={PAYMENT_TYPE_METHODS_TRANSLATION_MAP}
            />
            <SelectFormField<CustomerPaymentMethodFormSchemaValues>
                form={form}
                name="is_primary"
                isBoolean={true}
                required={true}
                label={formatMessage(paymentMethodsMessages.isPrimary)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <InputFormField<CustomerPaymentMethodFormSchemaValues>
                form={form}
                name="token"
                required={false}
                label={formatMessage(paymentMethodsMessages.token)}
            />
            <SearchSelectFormField
                name="customer"
                control={form.control}
                label={formatMessage(customersMessages.customer)}
                items={customersData?.results ?? []}
                clearable
                placeholder={`${formatMessage(customersMessages.customer)}...`}
                searchPlaceholder={formatMessage(customersMessages.searchCustomer)}
                emptyTitle={formatMessage(customersMessages.noCustomer)}
                emptyDescription={formatMessage(customersMessages.noCustomerFound)}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.name}
                getItemKeywords={(customer) => [customer.name]}
                disabled={disableCustomerSelect}
                loadOptions={setSearchCustomer}
            />
        </Form>
    );
}

export default CustomerPaymentMethodForm;
